import { Component, computed, HostListener, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { UwSidebar } from '../sidebar/uw-sidebar';
import { UwTopbar } from '../topbar/uw-topbar';
import { UwWorkflowStepper } from '../../shared/ui/workflow-stepper/uw-workflow-stepper';
import { UwDrawer } from '../../shared/ui/drawer/uw-drawer';
import { UwCommandPalette } from '../command-palette/uw-command-palette';
import { UwDialog } from '../../shared/ui/dialog/uw-dialog';
import { UwToastHost } from '../../shared/ui/toast-host/uw-toast-host';
import { UwAiFeedbackPopover } from '../../shared/domain/ai-feedback-popover/uw-ai-feedback-popover';
import { WorkbenchStore } from '../../core/stores/workbench.store';
import { CopilotStore } from '../../core/stores/copilot.store';
import { QueueStore } from '../../core/stores/queue.store';
import { CommandService } from '../../core/services/command.service';
import { NavigationService } from '../../core/services/navigation.service';
import { WORKFLOW_STEPS } from '../../core/config/navigation.config';
import type { WorkflowStep } from '../../core/models/navigation.model';

@Component({
  selector: 'uw-app-shell',
  standalone: true,
  imports: [RouterOutlet, UwSidebar, UwTopbar, UwWorkflowStepper, UwDrawer, UwCommandPalette, UwDialog, UwToastHost, UwAiFeedbackPopover],
  template: `
    <div class="flex h-screen overflow-hidden">
      <div class="hidden lg:block">
        <uw-sidebar />
      </div>

      <div class="flex min-w-0 flex-1 flex-col">
        <uw-topbar />
        @if (workbench.mode() === 'classic') {
          <div class="shrink-0 border-b border-line bg-surface px-4 py-2.5 sm:px-6">
            <uw-workflow-stepper [steps]="workflowSteps" [activeIndex]="activeWorkflowIndex()" />
          </div>
        }
        <main class="min-h-0 flex-1 overflow-y-auto">
          <router-outlet />
        </main>
      </div>
    </div>

    <uw-drawer [open]="workbench.mobileNavOpen()" (openChange)="workbench.setMobileNav($event)" side="left" label="Navigation">
      <div class="w-72">
        <uw-sidebar [expanded]="true" />
      </div>
    </uw-drawer>

    <uw-command-palette />
    <uw-dialog />
    <uw-ai-feedback-popover />
    <uw-toast-host />
  `,
})
export class AppShell {
  protected readonly workbench = inject(WorkbenchStore);
  private readonly copilot = inject(CopilotStore);
  private readonly queue = inject(QueueStore);
  private readonly command = inject(CommandService);
  private readonly router = inject(Router);
  private readonly nav = inject(NavigationService);

  protected readonly workflowSteps: WorkflowStep[] = WORKFLOW_STEPS;

  private readonly currentUrl = signal(this.router.url);

  protected readonly activeWorkflowIndex = computed(() => {
    const url = this.currentUrl();
    if (url.startsWith('/classic/account')) {
      const section = new URLSearchParams(url.split('?')[1] ?? '').get('section') ?? 'profile';
      const item = this.nav.flatten().find((i) => i.id === section);
      return item ? Math.max(0, this.nav.stepIndexForNavId(item.id)) : 1;
    }
    const item = this.nav.flatten().find((i) => url.startsWith(i.route));
    if (!item) return 0;
    return Math.max(0, this.nav.stepIndexForNavId(item.id));
  });

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = e.urlAfterRedirects;
        this.currentUrl.set(url);
        if (url.startsWith('/classic')) {
          this.workbench.setMode('classic');
        } else if (url.startsWith('/workbench')) {
          this.workbench.setMode('copilot');
          this.copilot.setView(url.startsWith('/workbench/account') ? 'account' : 'triage');
          if (url === '/workbench') this.queue.setActive(null);
        }
      });
  }

  @HostListener('document:keydown', ['$event'])
  protected onGlobalKeydown(event: KeyboardEvent): void {
    const isCmd = event.metaKey || event.ctrlKey;
    if (isCmd && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.command.openPalette();
      return;
    }
    if (event.altKey && event.key.toLowerCase() === 'c') {
      event.preventDefault();
      this.copilot.setView('triage');
      this.workbench.setMode('copilot');
      void this.router.navigate(['/workbench']);
      return;
    }
    if (event.altKey && event.key.toLowerCase() === 'l') {
      event.preventDefault();
      this.workbench.setMode('classic');
      void this.router.navigate(['/classic/submissions']);
    }
  }
}
